import React from "react";
import { Button, Spinner, Badge } from "reactstrap";

import { fetchPaymentRefundDetail } from "../../api/requests/payment-requests";
import { CARD_BRAND_BANK_LABELS } from "../../utils/paymentLinkUtils";
import { PAYER_WORDS } from "../../utils/paymentWord";

import {
    formatAmount,
    formatForeignCurrency,
    formatDateTime,
    getPaymentStatusText,
    getReceiptTypeText,
    getPaymentSourceText,
    statusToChipClass,
    copyToClipboard,
} from "../Tables/Paymenthistoryutils";
import { refundActorLabel } from "../../utils/refundActor";

/**
 * PaymentDetailPanel – Ödeme Geçmişi listesinde seçilen ödemenin sağdan açılan detay paneli.
 *
 * Temel veriyi listedeki satırdan alır. Tek istisna iade geçmişidir: ödeme geçmişi
 * listesi iade SATIRLARINI taşımaz (taşısaydı her satır için ek sorgu gerekirdi), bu
 * yüzden panel açıldığında yalnız o ödeme için tek bir detay isteği atılır.
 * Aksiyonlar listedeki mevcut fonksiyonlara delege edilir (yeni iş mantığı yok).
 *
 * Props
 * @param {object|null} payment      – Seçili ödeme (PaymentHistoryDTO satırı)
 * @param {Function} onClose         – Paneli kapat
 * @param {object} permissions       – { canToggleAccounted, canRefund, isRefundable, isSuperAdmin }
 * @param {object} actions           – { onViewReceipt, onDownloadPdf, onSendEmail, onToggleAccounted, onRefund, onDelete, onOpenFullDetail }
 * @param {boolean} pdfLoading       – Bu ödeme için makbuz indirme sürüyor mu
 * @param {boolean} accountingLoading– Bu ödeme için muhasebe güncellemesi sürüyor mu
 */
const PaymentDetailPanel = ({
    payment,
    onClose,
    permissions = {},
    actions = {},
    pdfLoading = false,
    accountingLoading = false,
    words = PAYER_WORDS,
}) => {
    const [copied, setCopied] = React.useState(false);
    const [refundDetail, setRefundDetail] = React.useState(null);
    const [refundLoading, setRefundLoading] = React.useState(false);

    const paymentId = payment?.id;

    React.useEffect(() => {
        if (!paymentId) { setRefundDetail(null); return; }
        let alive = true;
        setRefundLoading(true);
        setRefundDetail(null);
        fetchPaymentRefundDetail(paymentId)
            .then(res => { if (alive && res?.statusCode === 200) setRefundDetail(res.data); })
            .catch(() => { /* iade geçmişi alınamazsa bölüm gizlenir, panelin geri kalanı çalışır */ })
            .finally(() => { if (alive) setRefundLoading(false); });
        return () => { alive = false; };
    }, [paymentId]);

    if (!payment) return null;

    const { canToggleAccounted, canRefund, isRefundable, isSuperAdmin } = permissions;
    const { onViewReceipt, onDownloadPdf, onSendEmail, onToggleAccounted, onRefund, onDelete, onOpenFullDetail } = actions;

    const handleCopy = async () => {
        await copyToClipboard(payment.merchantOid);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const InfoRow = ({ icon, label, children }) => (
        <div className="pd-info-row">
            <span className="pd-info-label">
                <i className={`bx ${icon}`} /> {label}
            </span>
            <span className="pd-info-value">{children}</span>
        </div>
    );

    return (
        <div className="pd-panel">
            <div className="pd-panel-header">
                <h6 className="mb-0 fw-bold text-dark">{words.Word} Detayı</h6>
                <button className="btn btn-sm btn-link text-muted p-0" onClick={onClose} title="Kapat">
                    <i className="bx bx-x fs-4" />
                </button>
            </div>

            <div className="pd-panel-body">
                <span className={`chip ${statusToChipClass(payment.status)}`}>
                    {getPaymentStatusText(payment.status)}
                </span>

                <div className="pd-amount">{formatAmount(payment.amount)}</div>

                <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="text-muted small">İşlem No: {payment.merchantOid}</span>
                    <button className="btn btn-sm btn-link p-0 text-info" onClick={handleCopy} title="Kopyala">
                        <i className={`bx ${copied ? "bx-check text-success" : "bx-copy"}`} />
                    </button>
                </div>

                <div className="pd-info-list">
                    <InfoRow icon="bx-user" label="Ödeme Yapan">
                        {payment.userNameSnapshot || "Misafir"}
                    </InfoRow>

                    <InfoRow icon="bx-envelope" label="E-posta">
                        <span className="text-truncate d-inline-block" style={{ maxWidth: 180 }} title={payment.emailSnapshot || "-"}>
                            {payment.emailSnapshot || "-"}
                        </span>
                    </InfoRow>

                    <InfoRow icon="bx-buildings" label="Firma">
                        {payment.companyName || "-"}
                    </InfoRow>

                    {payment.accountName && (
                        <InfoRow icon="bx-id-card" label="Cari">
                            {payment.accountName}
                        </InfoRow>
                    )}

                    <InfoRow icon="bx-credit-card" label={`${words.Word} Yöntemi`}>
                        {getReceiptTypeText(payment.receiptType)}
                    </InfoRow>

                    {/* Elle girilen hareketlerin tipi yok; satırı tire ile
                        göstermek yerine hiç basmıyoruz (listedeki davranışla aynı). */}
                    {payment.paymentSource && (
                        <InfoRow icon="bx-link" label="Tahsilat Tipi">
                            {getPaymentSourceText(payment.paymentSource)}
                        </InfoRow>
                    )}

                    {payment.installmentCount > 1 && (
                        <InfoRow icon="bx-layer" label="Taksit">
                            {payment.installmentCount} taksit
                        </InfoRow>
                    )}

                    {/* Elle/nakit tahsilatlarda sanal POS yoktur; satır hiç basılmaz. */}
                    {payment.virtualPos && (
                        <InfoRow icon="bx-server" label="Sanal POS">
                            {payment.virtualPosLabel}
                        </InfoRow>
                    )}

                    {/* Kart markası/tipi BIN sorgusundan gelir; elle ve nakit
                        tahsilatlarda boş olduğu için koşullu gösterilir. */}
                    {payment.cardBrand && (
                        <InfoRow icon="bx-credit-card-front" label="Kart Markası">
                            {CARD_BRAND_BANK_LABELS[payment.cardBrand] || payment.cardBrand}
                        </InfoRow>
                    )}

                    {payment.cardType && (
                        <InfoRow icon="bx-card" label="Kart Tipi">
                            {payment.cardType === "CREDIT" ? "Kredi Kartı"
                                : payment.cardType === "DEBIT" ? "Banka Kartı"
                                : payment.cardType}
                        </InfoRow>
                    )}

                    <InfoRow icon="bx-calendar" label="Tarih">
                        {formatDateTime(payment.paymentDate)}
                    </InfoRow>

                    <InfoRow icon="bx-check-shield" label="Muhasebeleştirildi">
                        <span className={`chip ${payment.accounted ? "chip-green" : "chip-gray"}`}>
                            {payment.accounted ? "Evet" : "Hayır"}
                        </span>
                    </InfoRow>
                </div>

                {/* Başarısızlık kutusu iki katmanlı: üstte kullanıcıya dönük Türkçe açıklama,
                    altta destek ekibinin ihtiyaç duyduğu teknik kod ve sağlayıcının ham metni.
                    Teknik alanlar backend'de yalnız SUPER_ADMIN'e gönderilir (diğer rollerde
                    null gelir), bu yüzden burada ayrıca rol kontrolü yapılmaz. */}
                {payment.status === "FAILED"
                    && (payment.failureUserMessage || payment.failedReasonMsg || payment.failedReasonCode) && (
                    <div className="pd-fail-box">
                        <div className="fw-semibold mb-1">
                            <i className="bx bx-error-circle me-1" /> Hata Nedeni
                        </div>
                        <div className="small">
                            {payment.failureUserMessage || payment.failedReasonMsg || payment.failedReasonCode}
                        </div>
                        {(payment.failedReasonCode || payment.failedReasonMsg) && (
                            <div className="pd-fail-tech">
                                {payment.failedReasonCode && (
                                    <div>Hata kodu: <strong>{payment.failedReasonCode}</strong></div>
                                )}
                                {payment.failedReasonMsg
                                    && payment.failedReasonMsg !== payment.failureUserMessage && (
                                    <div>Sağlayıcı mesajı: {payment.failedReasonMsg}</div>
                                )}
                                {payment.virtualPos && <div>Sanal POS: {payment.virtualPosLabel}</div>}
                            </div>
                        )}
                    </div>
                )}

                {/* İşlem Özeti — yalnızca ödeme kaydında gerçekten bulunan alanlar.
                    Komisyon/net tutar backend'de tutulmadığı için burada yer almıyor. */}
                <div className="pd-summary">
                    <div className="pd-summary-title">İşlem Özeti</div>

                    <div className="pd-summary-row">
                        <span>Tutar</span>
                        <span>{formatAmount(payment.amount)}</span>
                    </div>

                    <div className="pd-summary-row">
                        <span>Taksit</span>
                        <span>{payment.installmentCount > 1 ? `${payment.installmentCount} taksit` : "Tek çekim"}</span>
                    </div>

                    {payment.finalUsdRate > 0 && (
                        <div className="pd-summary-row">
                            <span>USD Yaklaşık</span>
                            <span>{formatForeignCurrency((payment.amount || 0) / payment.finalUsdRate, "USD")}</span>
                        </div>
                    )}

                    {payment.finalEurRate > 0 && (
                        <div className="pd-summary-row">
                            <span>EUR Yaklaşık</span>
                            <span>{formatForeignCurrency((payment.amount || 0) / payment.finalEurRate, "EUR")}</span>
                        </div>
                    )}

                    <div className="pd-summary-row pd-summary-total">
                        <span>Tahsil Edilen</span>
                        <span>{formatAmount(payment.amount)}</span>
                    </div>
                </div>

                {/* İade Özeti + Geçmişi — yalnızca bu ödemenin en az bir iade denemesi varsa.
                    Başarısız ve bekleyen denemeler de listelenir: onlar da denetim kaydıdır
                    ve "neden iade edilemedi" sorusunu yanıtlar. */}
                {refundLoading && (
                    <div className="pd-summary text-muted small">
                        <Spinner size="sm" /> İade geçmişi yükleniyor…
                    </div>
                )}

                {!refundLoading && refundDetail?.refunds?.length > 0 && (
                    <div className="pd-summary">
                        <div className="pd-summary-row">
                            <span className="fw-semibold">İade Özeti</span>
                            <span>
                                {/* Yazı rengi açıkça beyaz veriliyor: temanın gri rozetinde
                                    metin koyu kalıyor ve zeminle kontrastı düşük görünüyordu.
                                    Zemin rengi (secondary/info) bilinçli olarak korunuyor. */}
                                {refundDetail.paymentStatus === "REFUNDED" && (
                                    <Badge color="secondary" pill className="text-white">Tamamen İade Edildi</Badge>
                                )}
                                {refundDetail.paymentStatus === "PARTIALLY_REFUNDED" && (
                                    <Badge color="info" pill className="text-white">Kısmi İade</Badge>
                                )}
                            </span>
                        </div>
                        <div className="pd-summary-row">
                            <span>Toplam İade</span>
                            <span>{formatAmount(refundDetail.totalRefunded)}</span>
                        </div>
                        <div className="pd-summary-row">
                            <span>Kalan İade Edilebilir</span>
                            <span>{formatAmount(refundDetail.remainingRefundable)}</span>
                        </div>

                        <div className="pd-summary-row pd-summary-total">
                            <span>İade Geçmişi</span>
                            <span>{refundDetail.refunds.length} işlem</span>
                        </div>

                        {refundDetail.refunds.map(r => (
                            <div key={r.refundId} className="pd-info-row" style={{ alignItems: "flex-start" }}>
                                <span className="pd-info-label">
                                    {formatDateTime(r.completedAt || r.requestedAt)}
                                </span>
                                <span className="pd-info-value text-end">
                                    <div>
                                        <strong>{formatAmount(r.amount)}</strong>{" "}
                                        <Badge color={r.refundClass === "FULL" ? "dark" : "info"} pill>
                                            {r.refundClass === "FULL" ? "Tam İade" : "Kısmi İade"}
                                        </Badge>{" "}
                                        <Badge
                                            color={r.status === "SUCCESS" ? "success"
                                                : r.status === "FAILED" ? "danger" : "warning"}
                                            pill
                                        >
                                            {r.status === "SUCCESS" ? "Başarılı"
                                                : r.status === "FAILED" ? "Başarısız" : "Bekliyor"}
                                        </Badge>
                                    </div>
                                    <div className="text-muted small">
                                        Yapan: {refundActorLabel(r)}
                                    </div>
                                    {r.status === "SUCCESS" && r.referenceNo && (
                                        <div className="text-muted small">Ref: {r.referenceNo}</div>
                                    )}
                                    {r.status === "FAILED" && r.failureMessage && (
                                        <div className="text-muted small">
                                            {r.failureCode ? `${r.failureCode} — ` : ""}{r.failureMessage}
                                        </div>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Aksiyonlar — özetin hemen altında (referans tasarımdaki gibi) */}
                <div className="pd-actions">
                    {/* Tam detay, aksiyon yığınının başında: paneldeki özetten sonraki
                        doğal adım "her şeyi gör" olduğu için makbuz aksiyonlarının
                        üstünde duruyor. */}
                    {onOpenFullDetail && (
                        <Button
                            className="btn-rounded w-100 pd-full-link"
                            onClick={() => onOpenFullDetail(payment)}
                        >
                            <i className="bx bx-detail me-1" /> Tüm Detayları Gör
                        </Button>
                    )}

                    {/* Aksiyonlar yalnızca handler'ı verildiğinde gösterilir: panel,
                        makbuz/e-posta akışı olmayan ekranlarda da (ör. POS Rapor)
                        yeniden kullanılabilsin ve tıklanınca hiçbir şey yapmayan
                        buton kalmasın. */}
                    {(onViewReceipt || onDownloadPdf) && (
                        <div className="pd-receipt-actions">
                            {onViewReceipt && (
                                <Button
                                    className="btn-navy btn-rounded"
                                    onClick={() => onViewReceipt(payment)}
                                >
                                    <i className="bx bx-receipt me-1" /> Makbuz Görüntüle
                                </Button>
                            )}
                            {onDownloadPdf && (
                                <Button
                                    className="btn-navy btn-rounded"
                                    onClick={() => onDownloadPdf(payment)}
                                    disabled={pdfLoading}
                                >
                                    {pdfLoading ? <Spinner size="sm" /> : <i className="bx bx-download me-1" />} Makbuz İndir
                                </Button>
                            )}
                        </div>
                    )}

                    {onSendEmail && (
                        <Button
                            className="btn-navy btn-rounded w-100"
                            onClick={() => onSendEmail(payment)}
                        >
                            <i className="bx bx-envelope me-1" /> E-posta Gönder
                        </Button>
                    )}

                    {canToggleAccounted && (
                        <Button
                            className="btn-navy btn-rounded w-100"
                            onClick={() => onToggleAccounted?.(payment)}
                            disabled={accountingLoading || payment.status !== "SUCCESS"}
                            title={payment.status !== "SUCCESS" ? `Sadece başarılı ${words.plural} muhasebeleştirilebilir` : ""}
                        >
                            {accountingLoading ? <Spinner size="sm" /> : <i className="bx bx-book-content me-1" />}
                            {payment.accounted ? " Muhasebeden Çıkar" : " Muhasebeleştir"}
                        </Button>
                    )}

                    {canRefund && isRefundable && (
                        <Button
                            className="btn-orange btn-rounded w-100"
                            onClick={() => onRefund?.(payment)}
                        >
                            <i className="bx bx-undo me-1" /> Tutar İadesi
                        </Button>
                    )}

                    {/* Silme yetkisi backend'de SUPER_ADMIN ile sınırlı (DELETE /api/payments/history) */}
                    {isSuperAdmin && (
                        <Button
                            color="danger"
                            outline
                            className="btn-rounded w-100"
                            onClick={() => onDelete?.(payment)}
                        >
                            <i className="bx bx-trash me-1" /> İşlemi Sil
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentDetailPanel;
